/**
 * ARRIVING SIGNED IN, AND STAYING THAT WAY.
 *
 * The login lives on lightsos.com and the app lives on app.lightsos.com.
 * Those are different origins, and a Supabase session is kept in localStorage,
 * which is per origin. So signing in on the site does NOT sign you into the
 * app on its own: the app's client boots, finds nothing, and treats a person
 * who signed in two seconds ago as a stranger.
 *
 * There are two honest ways across and this file does both.
 *
 * THE HANDOVER, for the first arrival. The site redirects to the app with the
 * tokens in the FRAGMENT, after the hash. A fragment is never transmitted to a
 * server: it appears in no Netlify log, no proxy log, and no Referer header on
 * the way anywhere else. The app spends them immediately and strips the hash,
 * because a fragment is still visible in the address bar and still lands in
 * browser history. This is the same mechanism Supabase uses for its own magic
 * links.
 *
 * THE LOGIN FORM, for every arrival after that. Signing in on the app writes
 * the session straight to the app's own storage and no handover happens at
 * all. That is the main path; the handover only ever runs on the first click
 * out of the account page.
 *
 * The old access code handover is still read, and still ignored, so that a
 * customer clicking a link the site has not stopped sending yet does not get a
 * confusing token on screen. It is dropped, not used.
 */

import { supa } from './supa'
import { codeFromHash } from './handoff'

/** Everything one arrival can carry. Read once, in one place. */
export interface Handoff {
  /** True when a session was actually established from the tokens. */
  session: boolean
  /** The legacy shared access code, while the site is still sending it. */
  code: string | null
}

export interface HandoffTokens {
  accessToken: string
  refreshToken: string
}

/**
 * Tokens carried in a location hash, or null.
 *
 * Tolerant about shape and strict about content, for the same reason the
 * access code reader was: a hash can arrive percent encoded, with other
 * parameters around it, or with a stray slash from a redirect chain. Anything
 * that is not a plausible pair is refused rather than handed to setSession,
 * because a rejected token looks exactly like a broken login.
 */
export function tokensFromHash(hash: string | undefined | null): HandoffTokens | null {
  if (!hash) return null
  const raw = hash.startsWith('#') ? hash.slice(1) : hash

  const parts = new Map<string, string>()
  for (const part of raw.split('&')) {
    const eq = part.indexOf('=')
    if (eq < 0) continue
    try {
      parts.set(part.slice(0, eq), decodeURIComponent(part.slice(eq + 1)))
    } catch {
      // A malformed escape is not a token.
      return null
    }
  }

  const accessToken = (parts.get('access_token') ?? '').trim().replace(/\/+$/, '')
  const refreshToken = (parts.get('refresh_token') ?? '').trim().replace(/\/+$/, '')
  if (!accessToken || !refreshToken) return null

  // A floor, not a format. The server decides what is valid; this refuses the
  // things that could never be right so they are never spent.
  if (accessToken.length < 20 || accessToken.length > 4096) return null
  if (refreshToken.length < 8 || refreshToken.length > 4096) return null
  if (/[\s#?]/.test(accessToken) || /[\s#?]/.test(refreshToken)) return null

  return { accessToken, refreshToken }
}

/**
 * Spend whatever arrived in the address bar, once, and clean up after it.
 *
 * ONE READER. THAT IS THE WHOLE POINT OF THIS FUNCTION.
 *
 * There were two. An effect in App.tsx pulled the access code out of the hash
 * and then wiped the hash, and it was written when a hash only ever carried
 * #k=CODE. The site now sends the tokens AND the code together, and that
 * effect ran first: it took the code, erased everything, and by the time the
 * sign in looked there was nothing there. A contractor who had just typed his
 * password on the website arrived at the app and was asked to type it again.
 *
 * It hid on any browser that already had a session, because there the app was
 * signed in for an entirely different reason, which is why it only showed up
 * on a phone that had never been used before.
 *
 * So everything the hash can carry is read here, in one pass, before the hash
 * is destroyed. Anything else that wants something out of a URL has to come
 * through this function.
 */
export async function consumeHandoff(): Promise<Handoff> {
  const hash = window.location.hash
  const tokens = tokensFromHash(hash)
  const code = codeFromHash(hash)

  /*
   * The hash goes whether or not any of it worked, and it goes with
   * replaceState so credentials do not sit in history behind a back button.
   * Reading everything first means a failure cannot strand the rest.
   */
  if (hash) {
    window.history.replaceState(null, '', window.location.pathname + window.location.search)
  }
  if (!tokens) return { session: false, code }

  try {
    const { error } = await supa.auth.setSession({
      access_token: tokens.accessToken,
      refresh_token: tokens.refreshToken,
    })
    return { session: !error, code }
  } catch {
    // An expired or already spent pair. The person sees the login form, which
    // is the correct outcome and not an error worth explaining.
    return { session: false, code }
  }
}
